import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, Share2, ShoppingBag, Store as StoreIcon, X, Play, Volume2, VolumeX, ArrowLeft, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import ProductImage from "@/components/ProductImage";

export default function Reels() {
    const { t, lang, isRtl } = useLang();
    const { user } = useAuth();
    const { add } = useCart();
    const [reels, setReels] = useState([]);
    const [muted, setMuted] = useState(true);
    const containerRef = useRef(null);
    const videoRefs = useRef({});
    const viewedRef = useRef(new Set());
    const [searchParams] = useSearchParams();
    const targetReel = searchParams.get("r");

    useEffect(() => {
        api.get("/reels").then((r) => setReels(r.data));
    }, []);

    // Play only the reel intersecting viewport
    useEffect(() => {
        if (!containerRef.current || reels.length === 0) return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const id = entry.target.getAttribute("data-reel-id");
                const video = videoRefs.current[id];
                if (!video) return;
                if (entry.intersectionRatio >= 0.7) {
                    video.play().catch(() => {});
                    if (!viewedRef.current.has(id)) {
                        viewedRef.current.add(id);
                        api.post(`/reels/${id}/view`).catch(() => {});
                    }
                } else {
                    video.pause();
                }
            });
        }, { threshold: [0, 0.7, 1] });
        containerRef.current.querySelectorAll("[data-reel-id]").forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [reels]);

    // Scroll to a specific reel if targetReel provided
    useEffect(() => {
        if (!targetReel || reels.length === 0) return;
        const el = containerRef.current?.querySelector(`[data-reel-id="${targetReel}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [targetReel, reels]);

    const toggleLike = async (reelId) => {
        if (!user) { toast.error(t("loginRequired")); return; }
        try {
            const { data } = await api.post(`/reels/${reelId}/like`);
            setReels((rs) => rs.map((r) => r.reel_id === reelId ? { ...r, is_liked: data.liked, likes_count: data.likes_count } : r));
        } catch { toast.error("Error"); }
    };

    const shareReel = async (reel) => {
        const url = `${window.location.origin}/reels?r=${reel.reel_id}`;
        if (navigator.share) {
            try { await navigator.share({ title: reel.caption || "Reel", url }); return; } catch {}
        }
        try { await navigator.clipboard.writeText(url); toast.success("✓"); } catch {}
    };

    const buyNow = async (product_id) => {
        if (!user) { toast.error(t("loginRequired")); return; }
        try {
            await add(product_id, 1);
            toast.success(t("addToCart"));
        } catch { toast.error("Error"); }
    };

    return (
        <div className="fixed inset-0 bg-black z-50 overflow-hidden" data-testid="reels-page">
            {/* Top bar */}
            <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4">
                <Link to="/" className="p-2 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-colors" data-testid="reels-close">
                    {isRtl ? <X className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />}
                </Link>
                <div className="text-white font-black">{t("reels")}</div>
                <button onClick={() => setMuted((m) => !m)} className="p-2 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-colors" data-testid="mute-toggle" aria-label="mute">
                    {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
            </div>

            {/* Feed (snap scroll) */}
            <div
                ref={containerRef}
                className="h-full overflow-y-auto snap-y snap-mandatory scroll-hide"
                data-testid="reels-feed"
            >
                {reels.length === 0 ? (
                    <div className="h-full grid place-items-center text-white/70">{t("noResults")}</div>
                ) : (
                    reels.map((reel) => (
                        <ReelItem
                            key={reel.reel_id}
                            reel={reel}
                            muted={muted}
                            onLike={() => toggleLike(reel.reel_id)}
                            onShare={() => shareReel(reel)}
                            onBuy={buyNow}
                            registerVideo={(el) => { if (el) videoRefs.current[reel.reel_id] = el; }}
                            t={t}
                            lang={lang}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function ReelItem({ reel, muted, onLike, onShare, onBuy, registerVideo, t, lang }) {
    const [paused, setPaused] = useState(false);
    const videoRef = useRef(null);

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) { v.play().catch(() => {}); setPaused(false); }
        else { v.pause(); setPaused(true); }
    };

    const product = reel.product;
    const price = product?.discount_price || product?.price;

    return (
        <div className="h-screen w-full snap-start relative flex items-center justify-center" data-reel-id={reel.reel_id} data-testid={`reel-${reel.reel_id}`}
             style={{ backgroundImage: reel.thumbnail_url ? `url(${reel.thumbnail_url})` : undefined, backgroundSize: "cover", backgroundPosition: "center" }}>
            {/* Video */}
            <video
                ref={(el) => { videoRef.current = el; registerVideo(el); }}
                src={reel.video_url}
                poster={reel.thumbnail_url}
                loop
                muted={muted}
                playsInline
                preload="auto"
                className="w-full h-full object-cover cursor-pointer"
                onClick={togglePlay}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            {paused && (
                <button className="absolute inset-0 grid place-items-center bg-black/30" onClick={togglePlay} aria-label="Play">
                    <Play className="w-20 h-20 text-white/80 fill-white/80" />
                </button>
            )}

            {/* Gradient overlays */}
            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

            {/* Right/end action rail */}
            <div className="absolute end-3 bottom-32 flex flex-col items-center gap-5 text-white z-10">
                <button onClick={onLike} className="flex flex-col items-center gap-1" data-testid={`like-btn-${reel.reel_id}`} aria-label="Like">
                    <div className={`p-3 rounded-full ${reel.is_liked ? "bg-terracotta" : "bg-white/10 backdrop-blur-md"} hover:scale-110 transition-transform`}>
                        <Heart className={`w-6 h-6 ${reel.is_liked ? "fill-white" : ""}`} />
                    </div>
                    <div className="text-xs font-bold">{reel.likes_count || 0}</div>
                </button>
                <button onClick={onShare} className="flex flex-col items-center gap-1" data-testid={`share-btn-${reel.reel_id}`} aria-label="Share">
                    <div className="p-3 rounded-full bg-white/10 backdrop-blur-md hover:scale-110 transition-transform">
                        <Share2 className="w-6 h-6" />
                    </div>
                    <div className="text-xs font-bold">{t("share")}</div>
                </button>
                {reel.store && (
                    <Link to={`/store/${reel.store.slug}`} className="flex flex-col items-center gap-1" data-testid={`visit-store-${reel.reel_id}`}>
                        <div className="p-3 rounded-full bg-white/10 backdrop-blur-md hover:scale-110 transition-transform">
                            <StoreIcon className="w-6 h-6" />
                        </div>
                        <div className="text-xs font-bold">{t("visitStore")}</div>
                    </Link>
                )}
            </div>

            {/* Bottom info */}
            <div className="absolute inset-x-0 bottom-0 p-5 pb-8 text-white z-10">
                {reel.store && (
                    <Link to={`/store/${reel.store.slug}`} className="flex items-center gap-2 mb-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md grid place-items-center overflow-hidden">
                            {reel.store.logo_url ? (
                                <img src={fileUrl(reel.store.logo_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <StoreIcon className="w-5 h-5" />
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="font-bold">{reel.store.name}</span>
                            {reel.store.verified && <BadgeCheck className="w-4 h-4 text-terracotta fill-white" />}
                        </div>
                    </Link>
                )}
                {reel.caption && (
                    <p className="text-sm leading-relaxed max-w-md line-clamp-3 mb-3">{reel.caption}</p>
                )}
                {product && (
                    <div className="mt-3 flex items-center gap-3 bg-white/10 backdrop-blur-lg rounded-2xl p-3 max-w-md" data-testid={`product-card-${reel.reel_id}`}>
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                            <ProductImage
                                src={product.images?.[0]}
                                alt={product.title}
                                imageClassName="w-full h-full object-cover"
                                showLabel={false}
                            />
                        </div>
                        <Link to={`/products/${product.product_id}`} className="flex-1 min-w-0">
                            <div className="text-sm font-bold line-clamp-1">{product.title}</div>
                            <div className="text-terracotta font-black text-lg">{price?.toFixed(2)} <span className="text-xs font-medium">{t("SAR")}</span></div>
                        </Link>
                        <Button
                            onClick={() => onBuy(product.product_id)}
                            className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white font-bold h-10 px-4"
                            data-testid={`buy-now-${reel.reel_id}`}
                        >
                            <ShoppingBag className="w-4 h-4 me-1.5" />
                            {t("buyNow")}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
